import { MoreVertical, Settings2, Sparkles } from 'lucide-react';
import { useState } from 'react';

import { ElementProperties } from '@/components/properties/ElementProperties';
import { Layers } from '@/components/properties/Layers';
import { RowProperties } from '@/components/properties/RowProperties';
import { SlideProperties } from '@/components/properties/SlideProperties';
import { Button } from '@/components/ui/Button';
import { Section } from '@/components/ui/Section';
import { api } from '@/lib/api';
import type {
  ImageContent,
  LatexContent,
  ScriptElement,
} from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

type Tab = 'design' | 'regenerate' | 'advanced';

export function RightPanel() {
  const script = useEditorStore((s) => s.script);
  const selection = useEditorStore((s) => s.selection);
  const [tab, setTab] = useState<Tab>('design');

  if (!script) return <Aside title="Properties">{null}</Aside>;

  const slide =
    selection && 'slideId' in selection
      ? script.slides.find((s) => s.id === selection.slideId)
      : script.slides[0];

  if (!selection || !slide) {
    return (
      <Aside title="Properties" subtitle="NOTHING SELECTED">
        <div className="flex h-48 flex-col items-center justify-center gap-2 px-6 text-center text-xs text-fg-subtle">
          <Settings2 size={20} />
          <p>Select a slide, row, or element to edit its properties.</p>
        </div>
        {slide && <Layers slide={slide} />}
      </Aside>
    );
  }

  const subtitle =
    selection.kind === 'element'
      ? 'ELEMENT SELECTED'
      : selection.kind === 'row'
        ? 'ROW SELECTED'
        : selection.kind === 'column'
          ? 'COLUMN SELECTED'
          : 'SLIDE SELECTED';

  return (
    <Aside title="Properties" subtitle={subtitle}>
      <Tabs value={tab} onChange={setTab} />

      <div className="flex-1 overflow-y-auto scroll-thin">
        {tab === 'design' && <DesignTab />}
        {tab === 'regenerate' && <RegenerateTab />}
        {tab === 'advanced' && <AdvancedTab />}
      </div>

      <Layers slide={slide} />
    </Aside>
  );
}

function Aside({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <aside className="flex w-[300px] shrink-0 flex-col overflow-hidden border-l border-border bg-bg-panel">
      <div className="flex items-start justify-between px-4 pt-4 pb-3">
        <div>
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          {subtitle && (
            <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
              {subtitle}
            </p>
          )}
        </div>
        <button className="grid h-7 w-7 place-items-center rounded-md text-fg-muted hover:bg-bg-subtle">
          <MoreVertical size={14} />
        </button>
      </div>
      {children}
    </aside>
  );
}

function Tabs({ value, onChange }: { value: Tab; onChange: (t: Tab) => void }) {
  return (
    <div className="flex border-b border-border-subtle px-4">
      {(['design', 'regenerate', 'advanced'] as const).map((t) => (
        <button
          key={t}
          onClick={() => onChange(t)}
          className={cn(
            'relative -mb-px h-9 px-3 text-[13px] font-medium capitalize transition',
            value === t
              ? 'text-accent border-b-2 border-accent'
              : 'text-fg-muted hover:text-fg border-b-2 border-transparent',
          )}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

function DesignTab() {
  const script = useEditorStore((s) => s.script);
  const selection = useEditorStore((s) => s.selection);
  if (!script || !selection) return null;
  const slide = script.slides.find((s) => s.id === selection.slideId);
  if (!slide) return null;

  if (selection.kind === 'slide') return <SlideProperties slide={slide} />;

  const row = slide.rows.find((r) => r.id === selection.rowId);
  if (!row) return null;

  if (selection.kind === 'row' || selection.kind === 'column')
    return <RowProperties slideId={slide.id} row={row} />;

  const column = row.columns.find((c) => c.id === selection.columnId);
  const element = column?.elements.find((e) => e.id === selection.elementId);
  if (!column || !element) return null;

  return (
    <ElementProperties
      slideId={slide.id}
      rowId={row.id}
      columnId={column.id}
      element={element}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Regenerate                                                                 */
/* -------------------------------------------------------------------------- */

function RegenerateTab() {
  const script = useEditorStore((s) => s.script);
  const selection = useEditorStore((s) => s.selection);

  if (!script || !selection) return <RegenerateEmpty />;
  if (selection.kind !== 'element') return <RegenerateNeedsElement />;

  const slide = script.slides.find((s) => s.id === selection.slideId);
  const row = slide?.rows.find((r) => r.id === selection.rowId);
  const column = row?.columns.find((c) => c.id === selection.columnId);
  const element = column?.elements.find((e) => e.id === selection.elementId);
  if (!slide || !row || !column || !element) return <RegenerateEmpty />;

  const ctx = {
    slideId: slide.id,
    rowId: row.id,
    columnId: column.id,
  };

  if (element.type === 'image')
    return <RegenerateImage element={element} ctx={ctx} />;
  if (element.type === 'text' || element.type === 'latex')
    return <RegenerateText element={element} ctx={ctx} />;

  return (
    <Section title="Regenerate">
      <p className="rounded-md bg-bg-subtle px-3 py-3 text-xs text-fg-subtle">
        Regeneration isn&apos;t supported for{' '}
        <span className="font-medium text-fg">{element.type}</span> elements
        yet.
      </p>
    </Section>
  );
}

function RegenerateEmpty() {
  return (
    <Section title="Regenerate">
      <div className="rounded-md bg-bg-subtle px-3 py-6 text-center text-xs text-fg-subtle">
        Select an element on the canvas to regenerate it with AI.
      </div>
    </Section>
  );
}

function RegenerateNeedsElement() {
  return (
    <Section title="Regenerate">
      <div className="rounded-md bg-bg-subtle px-3 py-6 text-center text-xs text-fg-subtle">
        Pick a single element to regenerate.
        <br />
        Slides and layouts can&apos;t be regenerated from here.
      </div>
    </Section>
  );
}

/** Image regeneration — calls `/ai/generate-image` and replaces `content.src`. */
function RegenerateImage({
  element,
  ctx,
}: {
  element: ScriptElement;
  ctx: { slideId: string; rowId: string; columnId: string };
}) {
  const { updateElement } = useEditorActions();
  const current = (element.content as ImageContent | undefined) ?? { src: '' };

  const [prompt, setPrompt] = useState(current.alt ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRegenerate = async () => {
    if (!prompt.trim()) {
      setError('Describe what the image should show.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.ai.generateImage({
        prompt: prompt.trim(),
        aspectRatio: '16:9',
      });
      updateElement(ctx.slideId, ctx.rowId, ctx.columnId, element.id, {
        content: { ...current, src: res.url, alt: prompt.trim() },
      });
    } catch (err) {
      setError((err as Error).message ?? 'Could not regenerate image.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Regenerate image">
      {current.src && (
        <img
          src={current.src}
          alt={current.alt ?? ''}
          className="mb-3 w-full rounded-md border border-border object-cover"
          style={{ aspectRatio: '16 / 9' }}
        />
      )}

      <PromptArea
        label="Image prompt"
        placeholder="A flat illustration of Newton's apple falling, white background, minimal style"
        value={prompt}
        onChange={setPrompt}
        rows={3}
      />

      {error && <ErrorLine message={error} />}

      <Button
        variant="primary"
        size="md"
        className="mt-3 w-full"
        disabled={busy}
        onClick={handleRegenerate}
      >
        <Sparkles size={14} />
        {busy ? 'Regenerating…' : 'Regenerate'}
      </Button>
    </Section>
  );
}

/** Text / LaTeX regeneration via Claude (reuses the narration-text endpoint). */
function RegenerateText({
  element,
  ctx,
}: {
  element: ScriptElement;
  ctx: { slideId: string; rowId: string; columnId: string };
}) {
  const { updateElement } = useEditorActions();
  const script = useEditorStore((s) => s.script);

  const initialText = extractElementText(element);
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRegenerate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.ai.generateNarrationText({
        currentDraft: initialText,
        rewriteInstruction: instruction.trim() || undefined,
        tone: script?.metadata.tone,
        language: script?.metadata.language,
        targetSeconds: 8,
      });
      const next = stripQuotes(res.text.trim());
      const patch: Partial<ScriptElement> =
        element.type === 'latex'
          ? {
              content: {
                ...((element.content as LatexContent | undefined) ?? {
                  source: '',
                }),
                source: next,
              },
            }
          : { content: next };
      updateElement(
        ctx.slideId,
        ctx.rowId,
        ctx.columnId,
        element.id,
        patch,
      );
    } catch (err) {
      setError((err as Error).message ?? 'Could not regenerate text.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Regenerate text">
      <div className="mb-3 max-h-32 overflow-y-auto rounded-md bg-bg-subtle px-3 py-2 text-xs text-fg-muted whitespace-pre-wrap scroll-thin">
        {initialText || (
          <span className="italic text-fg-subtle">No existing text.</span>
        )}
      </div>

      <PromptArea
        label="Rewrite instruction"
        placeholder="Make it shorter / more formal / translate to Hindi…"
        value={instruction}
        onChange={setInstruction}
        rows={2}
      />

      {error && <ErrorLine message={error} />}

      <Button
        variant="primary"
        size="md"
        className="mt-3 w-full"
        disabled={busy}
        onClick={handleRegenerate}
      >
        <Sparkles size={14} />
        {busy ? 'Regenerating…' : 'Regenerate'}
      </Button>

      <p className="mt-2 text-[11px] text-fg-subtle">
        Leave the instruction empty to ask Claude for a polished rewrite.
      </p>
    </Section>
  );
}

function PromptArea({
  label,
  placeholder,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-fg-muted">
        {label}
      </label>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          'w-full resize-none rounded-md border border-border bg-bg-input px-3 py-2 text-sm text-fg placeholder:text-fg-subtle',
          'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15 transition-colors',
        )}
      />
    </div>
  );
}

function ErrorLine({ message }: { message: string }) {
  return (
    <p className="mt-2 rounded-md bg-red-50 px-2 py-1.5 text-[11px] text-red-700">
      {message}
    </p>
  );
}

/** Pulls the human-readable text out of a text/latex element regardless of how it was authored. */
function extractElementText(element: ScriptElement): string {
  if (element.type === 'latex') {
    const c = element.content as LatexContent | undefined;
    return c?.source ?? '';
  }
  const raw = element.content;
  if (typeof raw === 'string') return stripHtml(raw);
  return '';
}

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>(?=\s|$)/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .trim();
}

function stripQuotes(s: string): string {
  return s.replace(/^["“”'']+|["“”'']+$/g, '').trim();
}

function AdvancedTab() {
  return (
    <Section title="Advanced">
      <div className="rounded-md bg-bg-subtle px-3 py-6 text-center text-xs text-fg-subtle">
        Advanced controls (custom CSS, accessibility hints, data bindings) will
        live here.
      </div>
    </Section>
  );
}
